"use client";
import { AlgoliaProvider } from "@components/algolia/AlgoliaProvider";
import { TARGET_GROUP_FACET } from "@lib/constants/filtersConstants";
import { CallToActionButtons } from "@components/cms/call-to-action-buttons/CallToActionButtons";
import { RichText } from "@components/cms/rich-text-renderer/RichText";
import { Text } from "@components/cms/text/Text";
import { Banner } from "@components/commerce/categories/banner";
import { ProductListing } from "@components/commerce/product-listing/ProductListing";
import { FiltersController } from "@components/commerce/product-listing/filters/context/FiltersContextProvider";
import type { IPageProductListingPage } from "@contentful-api/types/contentful";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { getAdditionalSizesFilters } from "@lib/utils/plp/plpShoeSizes";
import { type InitialFiltersType, useFilterStore, useStoreApi, useStore } from "@store/index";
import { PageHeading } from "@ecco/ui/page-heading";
import { Box } from "@ecco/ui/box";
import { isEqual } from "instantsearch.js/es/lib/utils";
import { useLocale } from "next-intl";
import { useEffect } from "react";
import { Breadcrumbs } from "@components/commerce/categories/breadcrumbs/Breadcrumbs";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import type { BazaarvoiceGalleryModel } from "@lib/contentful/mappers/modules/bazaarvoiceGallery/types";
import type { TextModel } from "@lib/contentful/mappers/modules/text/types";
import type { Options as RichTextOptions } from "@components/cms/rich-text-renderer/richTextOptions";
import { convertPathnameToCategory } from "@components/commerce/helpers";
import styles from "./productListingPageStyles.module.css";
import { useBreadcrumbItems } from "@components/commerce/categories/breadcrumbs/useBreadcrumbItems";

const BazaarvoiceGalleryContainer = dynamic(
    () =>
        import("@components/cms/bazaarvoice-gallery-container/BazaarvoiceGalleryContainer").then(
            (mod) => mod.BazaarvoiceGalleryContainer
        ),
    { ssr: false }
);

type ProductListingPageProps = {
    page: IPageProductListingPage;
    initialFiltersList: InitialFiltersType;
    bazaarvoiceGallery?: BazaarvoiceGalleryModel;
    faq?: TextModel;
    englishSlug?: string | null;
};

const stripLocalePrefix = (path: string) => path.replace(/^\/[a-z]{2}-[A-Z]{2}/, "");

const faqRichTextOptions: RichTextOptions = { alignAccordionWithHeader: true };

export const ProductListingPage = ({
    page,
    initialFiltersList,
    bazaarvoiceGallery,
    faq,
    englishSlug,
}: ProductListingPageProps) => {
    const locale = useLocale();
    const pathname = usePathname();

    const { getMultiple } = useMicrocopy();

    const microcopies = getMultiple(CommonCMS.shared, [
        CommonCMS.generic.readMore,
        CommonCMS.generic.readLess,
    ]);

    const storeApi = useStoreApi();
    const productResultsCount = useFilterStore((state) => state.resultCount);
    const lastLoadedPage = useStore((state) => state.data.products.lastLoadedPage);

    useEffect(() => {
        const { data, setInitialFilters } = storeApi.getState();
        if (!isEqual(data.products.initialFilters, initialFiltersList)) {
            setInitialFilters(initialFiltersList);
        }
    }, [storeApi, initialFiltersList]);

    const fields = page.fields;
    const {
        title,
        desc,
        filtersList = [],
        widgetSize = "Small",
        titleSize = "xlarge",
        descriptionSize = "normal",
        editorialWidgets,
        callToActions,
        sortByOptionsList = [],
        categoriesBanner,
    } = fields ?? {};

    const bannerItems = categoriesBanner?.fields?.links ?? [];

    const listOfFilters = [...filtersList, ...getAdditionalSizesFilters(filtersList)];
    const facetAttributes = [
        TARGET_GROUP_FACET,
        ...listOfFilters.map((filter) => filter.fields.facetName).filter(Boolean),
    ];

    const cleanedPathname = stripLocalePrefix(pathname);
    const localizedPageCategory = convertPathnameToCategory(cleanedPathname);
    const enPageCategory = convertPathnameToCategory(stripLocalePrefix(englishSlug ?? ""));
    const plpPathSegments = decodeURIComponent(cleanedPathname).split("/").filter(Boolean);
    const breadcrumbItems = useBreadcrumbItems({ pathSegments: plpPathSegments, locale });

    return (
        <>
            <Breadcrumbs items={breadcrumbItems} marginY={6} pl={[3, 3, 4, 6]} zIndex={3} />
            <main className={styles.mainContainer}>
                <PageHeading
                    title={title}
                    description={<RichText data={desc} />}
                    showMoreLabel={microcopies[CommonCMS.generic.readMore]}
                    showLessLabel={microcopies[CommonCMS.generic.readLess]}
                    titleSize={titleSize}
                    descriptionSize={descriptionSize}
                    resultsCount={productResultsCount}
                />
                <CallToActionButtons
                    callToActions={callToActions}
                    promotion={{ id: page.sys.id, title }}
                    px={[3, 3, 4, 6]}
                />
                <Box ml={[3, 3, 4, 6]}>
                    <Banner bannerItems={bannerItems} />
                </Box>
                <AlgoliaProvider locale={locale} facetAttributes={facetAttributes}>
                    <FiltersController
                        listOfFilters={listOfFilters}
                        sortByOptionsList={sortByOptionsList}
                        page={lastLoadedPage}
                        localizedPageCategory={localizedPageCategory}
                        enPageCategory={enPageCategory}
                    >
                        <ProductListing
                            listOfFilters={listOfFilters}
                            locale={locale}
                            widgetSize={widgetSize}
                            editorialWidgets={editorialWidgets}
                        />
                    </FiltersController>
                </AlgoliaProvider>
                {bazaarvoiceGallery && <BazaarvoiceGalleryContainer {...bazaarvoiceGallery} />}
                {faq && (
                    <Box px={[3, 3, 4, 6]} py={[8, 8, 12]}>
                        <Text {...faq} richTextOptions={faqRichTextOptions} />
                    </Box>
                )}
            </main>
        </>
    );
};
